/**
 * AirportIdentityStep - Step 1 of the Location Wizard
 *
 * Airport selection via autocomplete that auto-fills:
 * - Location name (from airport name)
 * - Location code (from ICAO code)
 * - Various other fields propagated to later steps
 *
 * User can override any auto-filled value.
 */

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AirportAutocomplete } from '@/components/locations/AirportAutocomplete'
import { useLocationWizardStore } from '@/stores/locationWizardStore'
import type { LocationStatus } from '@/types/locations'

export function AirportIdentityStep() {
  const {
    airport_code,
    name,
    location_code,
    status,
    setSelectedAirport,
    setName,
    setLocationCode,
    setStatus,
  } = useLocationWizardStore()

  return (
    <div className="space-y-4">
      {/* Airport Selection */}
      <div className="space-y-2">
        <Label>
          Airport <span className="text-destructive">*</span>
        </Label>
        <AirportAutocomplete
          value={airport_code}
          onSelect={setSelectedAirport}
          placeholder="Search by code or city..."
        />
        <p className="text-sm text-muted-foreground">
          Select an airport to auto-fill location details
        </p>
      </div>

      {/* Location Name */}
      <div className="space-y-2">
        <Label>
          Location Name <span className="text-destructive">*</span>
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Phoenix Sky Harbor International"
        />
        <p className="text-sm text-muted-foreground">
          Auto-filled from airport name, you can customize it
        </p>
      </div>

      {/* Location Code */}
      <div className="space-y-2">
        <Label>Location Code</Label>
        <Input
          value={location_code}
          onChange={(e) => setLocationCode(e.target.value)}
          placeholder="KPHX"
          className="font-mono"
        />
        <p className="text-sm text-muted-foreground">
          Auto-filled from airport ICAO code, you can customize it
        </p>
      </div>

      {/* Status */}
      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={status}
          onValueChange={(val) => setStatus(val as LocationStatus)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="coming_soon">Coming Soon</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
